const DEFAULT_TIMESLICE_MS = 2000;
const MONITOR_INTERVAL_MS = 125;
const SYSTEM_RMS_THRESHOLD = 0.008;
const MIC_RMS_THRESHOLD = 0.003;
const SYSTEM_HOLD_MS = 600;
const AudioContextCtor = typeof window !== 'undefined' ? (window.AudioContext || window.webkitAudioContext) : null;

const stopTracks = (stream) => {
  if (!stream) return;
  stream.getTracks().forEach((track) => {
    try {
      track.stop();
    } catch (error) {
      console.warn('Failed to stop track', error);
    }
  });
};

export class LiveRecorder {
  constructor({ timesliceMs = DEFAULT_TIMESLICE_MS, onChunk, onLevels, mode = 'mix' }) {
    this.timesliceMs = timesliceMs;
    this.onChunk = onChunk;
    this._onLevels = typeof onLevels === 'function' ? onLevels : null;
    this.sequence = 0;
    this._sources = [];
    this._mediaRecorder = null;
    this._mixedStream = null;
    this._audioContext = null;
    this._active = false;
    this._mimeType = null;
    this._mode = mode;
    this._monitorInterval = null;
    this._micChain = null;
    this._systemChain = null;
    this._levelBuffers = null;
    this._byteLevelBuffers = null;
    this._preferredSource = null;
    this._lastSystemActive = 0;
    this._simpleChains = null;
  }

  #selectMimeType() {
    if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') {
      return null;
    }
    const ua = navigator?.userAgent ?? '';
    const isSafari = /safari/i.test(ua) && !/chrome|crios|android/i.test(ua);
    const candidates = isSafari
      ? [
          'audio/mp4;codecs=mp4a.40.2',
          'audio/mp4',
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/ogg;codecs=opus',
        ]
      : [
          'audio/webm;codecs=opus',
          'audio/webm',
          'audio/mp4;codecs=mp4a.40.2',
          'audio/mp4',
          'audio/ogg;codecs=opus',
        ];
    const supported = candidates.find((type) => window.MediaRecorder.isTypeSupported?.(type));
    return supported ?? null;
  }

  async start({ useMic = true, useSystemAudio = false, mode, streams } = {}) {
    if (this._active) {
      throw new Error('Recorder already running');
    }
    if (!useMic && !useSystemAudio) {
      throw new Error('Mindestens eine Audioquelle auswählen.');
    }

    if (mode) {
      this._mode = mode;
    }

    this.ensureSupport({ useMic, useSystemAudio, streams });
    this.#stopRoutingMonitor();
    this.#emitLevels({ system: 0, mic: 0, preferred: useSystemAudio ? 'system' : 'mic' });

    try {
      await this.#setupStreams({ useMic, useSystemAudio, streams });
      this._active = true;
      this.sequence = 0;
      const mimeType = this.#selectMimeType();
      this._mimeType = mimeType;
      const options = mimeType
        ? { mimeType, audioBitsPerSecond: 128000 }
        : { audioBitsPerSecond: 128000 };

      this._mediaRecorder = new MediaRecorder(this._mixedStream, options);

      this._mediaRecorder.addEventListener('dataavailable', async (event) => {
        if (!this._active) return;
        if (!event.data || event.data.size === 0) return;
        const chunk = {
          sequence: this.sequence++,
          blob: event.data,
          durationMs: this.timesliceMs,
          createdAt: new Date().toISOString(),
          mimeType: event.data.type || this._mimeType || 'audio/webm',
        };
        if (typeof this.onChunk === 'function') {
          try {
            await this.onChunk(chunk);
          } catch (error) {
            console.error('Live chunk callback failed', error);
          }
        }
      });

      this._mediaRecorder.start(this.timesliceMs);
    } catch (error) {
      this.stop();
      throw error;
    }
  }

  stop() {
    this._active = false;
    this.#stopRoutingMonitor();
    if (this._mediaRecorder && this._mediaRecorder.state !== 'inactive') {
      try {
        this._mediaRecorder.stop();
      } catch (error) {
        console.warn('Failed to stop media recorder', error);
      }
    }
    this._mediaRecorder = null;
    this._mimeType = null;
    this._sources.forEach(stopTracks);
    this._sources = [];
    if (this._audioContext) {
      try {
        this._audioContext.close();
      } catch (error) {
        console.warn('Failed to close audio context', error);
      }
      this._audioContext = null;
    }
    this._mixedStream = null;
    this._micChain = null;
    this._systemChain = null;
    this._levelBuffers = null;
    this._byteLevelBuffers = null;
    this._preferredSource = null;
    this._lastSystemActive = 0;
    this._simpleChains = null;
    this.#emitLevels({ system: 0, mic: 0, preferred: 'mic' });
  }

  async #setupStreams({ useMic, useSystemAudio, streams } = {}) {
    let micStream = streams?.micStream ?? null;
    let systemStream = streams?.systemStream ?? null;

    if (useSystemAudio && !systemStream) {
      systemStream = await navigator.mediaDevices.getDisplayMedia({
        audio: true,
        video: true,
      });
    }

    if (!useSystemAudio && systemStream) {
      stopTracks(systemStream);
      systemStream = null;
    }

    if (useMic && !micStream) {
      micStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
        },
      });
    }

    if (!useMic && micStream) {
      stopTracks(micStream);
      micStream = null;
    }

    if (useSystemAudio && systemStream && !systemStream.getAudioTracks().length) {
      stopTracks(systemStream);
      systemStream = null;
      if (!micStream) {
        throw new Error('Systemaudio nicht verfügbar.');
      }
    }

    const activeStreams = [];
    if (micStream) {
      activeStreams.push({ type: 'mic', stream: micStream });
    }
    if (systemStream) {
      activeStreams.push({ type: 'system', stream: systemStream });
    }

    if (!activeStreams.length) {
      throw new Error('Keine Audioquelle verfügbar.');
    }

    this._sources = activeStreams.map((entry) => entry.stream);

    if (!AudioContextCtor) {
      const fallback = activeStreams[0];
      this._mixedStream = fallback.stream;
      this._preferredSource = fallback.type;
      this.#emitLevels({
        system: fallback.type === 'system' ? 0 : 0,
        mic: fallback.type === 'mic' ? 0 : 0,
        preferred: this._preferredSource ?? 'mic',
      });
      return;
    }

    this._audioContext = new AudioContextCtor();
    try {
      await this._audioContext.resume();
    } catch (error) {
      console.warn('AudioContext resume failed', error);
    }

    if (this._mode === 'prefer-system' && micStream && systemStream) {
      this.#setupPreferSystemMix({ micStream, systemStream });
      return;
    }

    const destination = this._audioContext.createMediaStreamDestination();
    this._levelBuffers = {};
    this._byteLevelBuffers = {};
    const chains = activeStreams.map(({ type, stream }) => {
      const sourceNode = this._audioContext.createMediaStreamSource(stream);
      const analyser = this._audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.7;
      sourceNode.connect(analyser);
      analyser.connect(destination);
      this._levelBuffers[type] = new Float32Array(analyser.fftSize);
      return { type, analyser };
    });

    this._mixedStream = destination.stream;
    this._simpleChains = chains;
    this._preferredSource = chains.some((chain) => chain.type === 'system') ? 'system' : 'mic';
    this.#emitLevels({ system: 0, mic: 0, preferred: this._preferredSource });
    this.#startSimpleLevelMonitor();
  }

  #setupPreferSystemMix({ micStream, systemStream }) {
    if (!this._audioContext) {
      throw new Error('AudioContext not initialised');
    }
    const destination = this._audioContext.createMediaStreamDestination();

    const createChain = (stream) => {
      const source = this._audioContext.createMediaStreamSource(stream);
      const analyser = this._audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.7;
      const gain = this._audioContext.createGain();
      gain.gain.value = 0;
      source.connect(analyser);
      analyser.connect(gain);
      gain.connect(destination);
      return { analyser, gain };
    };

    this._micChain = createChain(micStream);
    this._systemChain = createChain(systemStream);
    this._levelBuffers = {
      mic: new Float32Array(this._micChain.analyser.fftSize),
      system: new Float32Array(this._systemChain.analyser.fftSize),
    };
    this._byteLevelBuffers = {};
    this._mixedStream = destination.stream;
    this._preferredSource = 'system';
    this._lastSystemActive = Date.now();
    this.#setSourceGains('system', true);
    this.#emitLevels({ system: 0, mic: 0, preferred: this._preferredSource });
    this.#startRoutingMonitor();
  }

  #setSourceGains(preferred, immediate = false) {
    if (!this._audioContext) return;
    const now = this._audioContext.currentTime ?? 0;
    const duration = immediate ? 0 : 0.05;

    const rampGain = (chain, value) => {
      if (!chain) return;
      const param = chain.gain.gain;
      try {
        param.cancelScheduledValues(now);
        if (duration === 0) {
          param.setValueAtTime(value, now);
        } else {
          param.setValueAtTime(param.value, now);
          param.linearRampToValueAtTime(value, now + duration);
        }
      } catch (error) {
        param.value = value;
      }
    };

    rampGain(this._systemChain, preferred === 'system' ? 1 : 0);
    rampGain(this._micChain, preferred === 'mic' ? 1 : 0);
    this._preferredSource = preferred;
  }

  #startRoutingMonitor() {
    if (!this._systemChain || !this._micChain || !this._levelBuffers) {
      return;
    }
    this.#clearMonitorInterval();
    this._monitorInterval = setInterval(() => {
      if (!this._active) {
        return;
      }
      const systemLevel = this.#computeRms(this._systemChain.analyser, this._levelBuffers.system, 'system');
      const micLevel = this.#computeRms(this._micChain.analyser, this._levelBuffers.mic, 'mic');
      const now = Date.now();

      if (systemLevel >= SYSTEM_RMS_THRESHOLD) {
        this._lastSystemActive = now;
        if (this._preferredSource !== 'system') {
          this.#setSourceGains('system');
        }
        this.#emitLevels({ system: systemLevel, mic: micLevel, preferred: this._preferredSource });
        return;
      }

      if (now - this._lastSystemActive > SYSTEM_HOLD_MS) {
        if (micLevel >= MIC_RMS_THRESHOLD && this._preferredSource !== 'mic') {
          this.#setSourceGains('mic');
        } else if (micLevel < MIC_RMS_THRESHOLD && this._preferredSource !== 'system') {
          this.#setSourceGains('system');
        }
      }
      this.#emitLevels({ system: systemLevel, mic: micLevel, preferred: this._preferredSource });
    }, MONITOR_INTERVAL_MS);
  }

  #clearMonitorInterval() {
    if (this._monitorInterval) {
      clearInterval(this._monitorInterval);
      this._monitorInterval = null;
    }
  }

  #stopRoutingMonitor() {
    this.#clearMonitorInterval();
    this._simpleChains = null;
    this._levelBuffers = null;
    this._byteLevelBuffers = null;
  }

  #startSimpleLevelMonitor() {
    if (!this._simpleChains || !this._levelBuffers) {
      return;
    }
    this.#clearMonitorInterval();
    this._preferredSource = this._preferredSource ?? (this._simpleChains.some(({ type }) => type === 'system') ? 'system' : 'mic');
    this.#emitLevels({ system: 0, mic: 0, preferred: this._preferredSource });
    this._monitorInterval = setInterval(() => {
      if (!this._active) {
        return;
      }
      let systemLevel = 0;
      let micLevel = 0;
      this._simpleChains.forEach(({ type, analyser }) => {
        const buffer = this._levelBuffers?.[type];
        const level = buffer ? this.#computeRms(analyser, buffer, type) : 0;
        if (type === 'system') {
          systemLevel = level;
        } else if (type === 'mic') {
          micLevel = level;
        }
      });

      const hasSystem = this._simpleChains.some(({ type }) => type === 'system');
      if (hasSystem) {
        if (systemLevel >= SYSTEM_RMS_THRESHOLD) {
          this._preferredSource = 'system';
        } else if (micLevel >= MIC_RMS_THRESHOLD) {
          this._preferredSource = 'mic';
        }
      } else {
        this._preferredSource = 'mic';
      }

      this.#emitLevels({
        system: hasSystem ? systemLevel : 0,
        mic: micLevel,
        preferred: this._preferredSource,
      });
    }, MONITOR_INTERVAL_MS);
  }

  #emitLevels(levels = {}) {
    if (!this._onLevels) return;
    const payload = {
      system: Math.max(0, levels.system ?? 0),
      mic: Math.max(0, levels.mic ?? 0),
      preferred: levels.preferred ?? this._preferredSource ?? 'mic',
    };
    try {
      this._onLevels(payload);
    } catch (error) {
      console.warn('Audio level callback failed', error);
    }
  }

  #computeRms(analyser, floatBuffer, key) {
    if (!analyser || !floatBuffer) {
      return 0;
    }
    if (analyser.getFloatTimeDomainData) {
      analyser.getFloatTimeDomainData(floatBuffer);
      let sum = 0;
      for (let i = 0; i < floatBuffer.length; i += 1) {
        const sample = floatBuffer[i];
        sum += sample * sample;
      }
      return Math.sqrt(sum / floatBuffer.length);
    }

    if (!this._byteLevelBuffers) {
      this._byteLevelBuffers = {};
    }
    let byteBuffer = this._byteLevelBuffers[key];
    if (!byteBuffer || byteBuffer.length !== floatBuffer.length) {
      byteBuffer = new Uint8Array(floatBuffer.length);
      this._byteLevelBuffers[key] = byteBuffer;
    }
    analyser.getByteTimeDomainData(byteBuffer);
    let sum = 0;
    for (let i = 0; i < byteBuffer.length; i += 1) {
      const normalized = (byteBuffer[i] - 128) / 128;
      sum += normalized * normalized;
    }
    return Math.sqrt(sum / byteBuffer.length);
  }

  ensureSupport({ useMic, useSystemAudio, streams } = {}) {
    if (typeof navigator === 'undefined') {
      throw new Error('Navigator API nicht verfügbar.');
    }
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices) {
      throw new Error('Dieser Browser unterstützt keine Audioaufnahme (MediaDevices fehlt).');
    }
    if (useMic && !streams?.micStream && typeof mediaDevices.getUserMedia !== 'function') {
      throw new Error('Dieser Browser unterstützt keine Audioaufnahme (MediaDevices.getUserMedia fehlt).');
    }
    if (useSystemAudio && !streams?.systemStream && typeof mediaDevices.getDisplayMedia !== 'function') {
      throw new Error('Systemaudio wird von diesem Browser nicht unterstützt (MediaDevices.getDisplayMedia fehlt).');
    }
  }
}

export const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Blob to base64 conversion failed'));
        return;
      }
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
